import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Text,
  Title,
  Metric,
  Table,
  TableHead,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  Button,
  Grid,
} from '@tremor/react';
import {
  BarChart as RechartsBarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LabelList,
} from 'recharts';
import kpiAPI from '../../api/kpi';
import DatePicker from './DatePicker';
import './KPIStatPage.css';

const PROJECT = 'MAN';

const STORAGE_KEY_INSPEKT_DATE_FROM = 'inspekt-dateFrom';
const STORAGE_KEY_INSPEKT_DATE_TO = 'inspekt-dateTo';

/** Usuwa prefix "C_" z wartości Dział */
function normalizeDzial(val) {
  if (val == null) return '';
  const s = String(val).trim();
  if (s.startsWith('C_')) return s.slice(2).trim();
  return s;
}

function getInitialDateFrom() {
  try {
    const s = localStorage.getItem(STORAGE_KEY_INSPEKT_DATE_FROM);
    if (s && /^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  } catch (e) {}
  const d = new Date();
  d.setDate(d.getDate() - 7);
  return formatDateForInput(d);
}

function getInitialDateTo() {
  try {
    const s = localStorage.getItem(STORAGE_KEY_INSPEKT_DATE_TO);
    if (s && /^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  } catch (e) {}
  return formatDateForInput(new Date());
}

function formatDateForInput(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function parseDateFromCell(val) {
  if (val == null || val === '') return null;
  const s = String(val).trim();
  if (!s) return null;
  const datePart = s.includes(' ') ? s.split(/\s/)[0] : s;
  const iso = /^\d{4}-\d{2}-\d{2}/.exec(datePart);
  if (iso) return new Date(iso[0]);
  const ddmmyy = datePart.match(/^(\d{1,2})[./](\d{1,2})[./](\d{4})$/);
  if (ddmmyy) return new Date(parseInt(ddmmyy[3], 10), parseInt(ddmmyy[2], 10) - 1, parseInt(ddmmyy[1], 10));
  const d = new Date(datePart);
  return Number.isNaN(d.getTime()) ? null : d;
}

function getDateColumnIndex(headers) {
  const names = headers.map((h) => String(h || '').trim().toUpperCase());
  const idx = names.findIndex(
    (n) => n === 'DATA' || n === 'DATE' || n.includes('DATA') || n.startsWith('DATA')
  );
  return idx >= 0 ? idx : 0;
}

function getColumnIndex(headers, headerName) {
  const needle = String(headerName || '').trim().toLowerCase();
  const idx = headers.findIndex((h) => String(h || '').trim().toLowerCase() === needle);
  return idx >= 0 ? idx : -1;
}

function getColumnIndexFlex(headers, ...names) {
  for (const name of names) {
    const idx = getColumnIndex(headers, name);
    if (idx >= 0) return idx;
  }
  for (const name of names) {
    const needle = String(name || '').trim().toLowerCase();
    if (needle) {
      const idx = headers.findIndex((h) => String(h || '').trim().toLowerCase().includes(needle));
      if (idx >= 0) return idx;
    }
  }
  return -1;
}

/** Filtruje wiersze po zakresie dat */
function filterRowsByDateRange(rows, headers, rangeFrom, rangeTo) {
  if (!rows.length || !rangeFrom || !rangeTo) return rows;
  const dateCol = getDateColumnIndex(headers);
  const from = new Date(rangeFrom);
  const to = new Date(rangeTo);
  to.setHours(23, 59, 59, 999);
  return rows.filter((row) => {
    const val = row[dateCol] && typeof row[dateCol] === 'object' ? row[dateCol].value : row[dateCol];
    const d = parseDateFromCell(val);
    if (!d) return false;
    const t = d.getTime();
    return t >= from.getTime() && t <= to.getTime();
  });
}

/** Wykres ilości wpisów wg czasu – liczba wierszy na dzień/miesiąc/rok */
function buildTimeChartDataByDateRange(filteredRows, headers, dateFrom, dateTo, rangeKey) {
  const dateCol = getDateColumnIndex(headers);
  if (dateCol < 0 || !dateFrom || !dateTo) return [];
  const from = new Date(dateFrom);
  const to = new Date(dateTo);
  to.setHours(23, 59, 59, 999);

  if (rangeKey === 'day') {
    const byDay = new Map();
    for (let d = new Date(from); d <= to; d.setDate(d.getDate() + 1)) {
      byDay.set(formatDateForInput(d), 0);
    }
    filteredRows.forEach((row) => {
      const val = row[dateCol] && typeof row[dateCol] === 'object' ? row[dateCol].value : row[dateCol];
      const d = parseDateFromCell(val);
      if (!d) return;
      const key = formatDateForInput(d);
      if (byDay.has(key)) byDay.set(key, (byDay.get(key) || 0) + 1);
    });
    return Array.from(byDay.entries()).map(([dateStr, value]) => ({
      label: dateStr,
      value,
      dateStr,
    }));
  }
  if (rangeKey === 'month') {
    const byMonth = new Map();
    let d = new Date(from.getFullYear(), from.getMonth(), 1);
    const end = new Date(to.getFullYear(), to.getMonth(), 1);
    while (d.getTime() <= end.getTime()) {
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      byMonth.set(key, { year: d.getFullYear(), month: d.getMonth() + 1, value: 0 });
      d = new Date(d.getFullYear(), d.getMonth() + 1, 1);
    }
    filteredRows.forEach((row) => {
      const val = row[dateCol] && typeof row[dateCol] === 'object' ? row[dateCol].value : row[dateCol];
      const cellDate = parseDateFromCell(val);
      if (!cellDate) return;
      const key = `${cellDate.getFullYear()}-${String(cellDate.getMonth() + 1).padStart(2, '0')}`;
      const entry = byMonth.get(key);
      if (entry) entry.value += 1;
    });
    return Array.from(byMonth.values()).map((e) => ({
      label: `${MONTH_LABELS[e.month - 1]} ${e.year}`,
      value: e.value,
      year: e.year,
      month: e.month,
    }));
  }
  if (rangeKey === 'year') {
    const years = new Set();
    for (let y = from.getFullYear(); y <= to.getFullYear(); y++) years.add(y);
    const byYear = new Map();
    years.forEach((y) => byYear.set(y, 0));
    filteredRows.forEach((row) => {
      const val = row[dateCol] && typeof row[dateCol] === 'object' ? row[dateCol].value : row[dateCol];
      const cellDate = parseDateFromCell(val);
      if (!cellDate || !byYear.has(cellDate.getFullYear())) return;
      byYear.set(cellDate.getFullYear(), (byYear.get(cellDate.getFullYear()) || 0) + 1);
    });
    return Array.from(byYear.entries()).map(([year, value]) => ({ label: String(year), value, year }));
  }
  return [];
}

function filterRowsByDateStr(rows, headers, dateStr) {
  const dateCol = getDateColumnIndex(headers);
  const [y, m, day] = dateStr.split('-').map(Number);
  return rows.filter((row) => {
    const val = row[dateCol] && typeof row[dateCol] === 'object' ? row[dateCol].value : row[dateCol];
    const d = parseDateFromCell(val);
    if (!d) return false;
    return d.getFullYear() === y && d.getMonth() === m - 1 && d.getDate() === day;
  });
}

function filterRowsByYear(rows, headers, year) {
  const dateCol = getDateColumnIndex(headers);
  return rows.filter((row) => {
    const val = row[dateCol] && typeof row[dateCol] === 'object' ? row[dateCol].value : row[dateCol];
    const d = parseDateFromCell(val);
    return d && d.getFullYear() === year;
  });
}

function filterRowsByMonth(rows, headers, year, month) {
  const dateCol = getDateColumnIndex(headers);
  return rows.filter((row) => {
    const val = row[dateCol] && typeof row[dateCol] === 'object' ? row[dateCol].value : row[dateCol];
    const d = parseDateFromCell(val);
    if (!d) return false;
    return d.getFullYear() === year && d.getMonth() === month - 1;
  });
}

const CHART_COLORS = [
  '#00bcd4', '#ff9800', '#4caf50', '#f44336', '#9c27b0', '#2196f3',
  '#ffeb3b', '#009688', '#e91e63', '#3f51b5',
];

const MONTH_LABELS = ['Sty', 'Lut', 'Mar', 'Kwi', 'Maj', 'Cze', 'Lip', 'Sie', 'Wrz', 'Paź', 'Lis', 'Gru'];

const ZAKRES_OPTIONS = [
  { key: 'day', label: 'Dzień' },
  { key: 'month', label: 'Miesiąc' },
  { key: 'year', label: 'Rok' },
];

/** Dane do wykresu Defekt (odpowiednik PS według kodowania) – kolumna Defekt / Defekt- Status defektu, liczba wpisów */
function buildDefektBarData(filteredRows, headers) {
  const defektCol = getColumnIndexFlex(headers, 'defekt', 'Defekt', 'Defekt- Status defektu', 'Status defektu');
  if (defektCol < 0) return [];
  const byDefekt = new Map();
  filteredRows.forEach((row) => {
    const val = row[defektCol] && typeof row[defektCol] === 'object' ? row[defektCol].value : row[defektCol];
    const name = String(val ?? '').trim() || '(pusty)';
    byDefekt.set(name, (byDefekt.get(name) || 0) + 1);
  });
  return Array.from(byDefekt.entries())
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);
}

/** Dane do wykresu Dział (odpowiednik PS według PSA) – kolumna Dział bez C_, liczba wpisów */
function buildDzialBarData(filteredRows, headers) {
  const dzialCol = getColumnIndexFlex(headers, 'dział', 'Dział', 'Dzial');
  if (dzialCol < 0) return [];
  const byDzial = new Map();
  filteredRows.forEach((row) => {
    const val = row[dzialCol] && typeof row[dzialCol] === 'object' ? row[dzialCol].value : row[dzialCol];
    const label = normalizeDzial(val) || '(pusty)';
    byDzial.set(label, (byDzial.get(label) || 0) + 1);
  });
  return Array.from(byDzial.entries())
    .map(([DZIAŁ, value]) => ({ DZIAŁ, value }))
    .sort((a, b) => b.value - a.value);
}

/** Filtruje wiersze według wyboru z wykresu (Defekt i/lub Dział) */
function filterRowsByBarSelection(rows, headers, selectedDefekt, selectedDzial) {
  if (!selectedDefekt && !selectedDzial) return rows;
  const defektCol = getColumnIndexFlex(headers, 'defekt', 'Defekt', 'Defekt- Status defektu', 'Status defektu');
  const dzialCol = getColumnIndexFlex(headers, 'dział', 'Dział', 'Dzial');
  return rows.filter((row) => {
    if (selectedDefekt && defektCol >= 0) {
      const val = row[defektCol] && typeof row[defektCol] === 'object' ? row[defektCol].value : row[defektCol];
      if (String(val ?? '').trim() !== selectedDefekt) return false;
    }
    if (selectedDzial && dzialCol >= 0) {
      const val = row[dzialCol] && typeof row[dzialCol] === 'object' ? row[dzialCol].value : row[dzialCol];
      if (normalizeDzial(val) !== selectedDzial) return false;
    }
    return true;
  });
}

function normalizeSheetData(data) {
  if (!data || !Array.isArray(data)) return [];
  return data.map((row) => {
    if (!Array.isArray(row)) return [{ value: '' }];
    return row.map((cell) => {
      if (cell && typeof cell === 'object' && 'value' in cell) return cell;
      return { value: cell != null ? String(cell) : '' };
    });
  });
}

function InspektTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const value = payload[0]?.value ?? '—';
  return (
    <div className="kpi-ps-tooltip">
      <div className="kpi-ps-tooltip-header">{label ?? '—'}</div>
      <div className="kpi-ps-tooltip-row">
        <span className="kpi-ps-tooltip-dot kpi-ps-tooltip-dot-cyan" />
        <span>Ilość: {value}</span>
      </div>
    </div>
  );
}

function DefektTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const value = payload[0]?.value;
  return (
    <div className="kpi-ps-tooltip">
      <div className="kpi-ps-tooltip-header">{label}</div>
      <div className="kpi-ps-tooltip-row">
        <span className="kpi-ps-tooltip-dot kpi-ps-tooltip-dot-cyan" />
        <span>Ilość wpisów: {value}</span>
      </div>
    </div>
  );
}

function DzialTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const value = payload[0]?.value ?? '—';
  return (
    <div className="kpi-ps-tooltip">
      <div className="kpi-ps-tooltip-header">{label ?? '—'}</div>
      <div className="kpi-ps-tooltip-row">
        <span className="kpi-ps-tooltip-dot kpi-ps-tooltip-dot-cyan" />
        <span>Ilość wpisów: {value}</span>
      </div>
    </div>
  );
}

const KPI_CARDS_KEYS = [
  { key: 'ilosc-wpisow', label: 'ILOŚĆ WPISÓW' },
  { key: 'defektow', label: 'DEFEKTÓW' },
  { key: 'dzialow', label: 'DZIAŁÓW' },
];

function Inspekt() {
  const [dateFrom, setDateFrom] = useState(getInitialDateFrom);
  const [dateTo, setDateTo] = useState(getInitialDateTo);
  const [rangeKey, setRangeKey] = useState('day');
  const [selectedTimeChartDateStr, setSelectedTimeChartDateStr] = useState(null);
  const [selectedTimeChartMonth, setSelectedTimeChartMonth] = useState(null);
  const [selectedTimeChartYear, setSelectedTimeChartYear] = useState(null);
  const [selectedDefekt, setSelectedDefekt] = useState(null);
  const [selectedDzial, setSelectedDzial] = useState(null);
  const [inspektSheet, setInspektSheet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadReports = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const res = await kpiAPI.getReports(PROJECT);
      const list = Array.isArray(res?.sheets) ? res.sheets : [];
      const insp = list.find((s) => String(s.name || '').trim().toUpperCase() === 'INSPEKT');
      setInspektSheet(insp ? { name: insp.name, data: normalizeSheetData(insp.data) } : null);
      if (!insp) setError('Nie udało się załadować danych z arkusza INSPEKT.');
    } catch (err) {
      console.error('Błąd ładowania raportu INSPEKT:', err);
      setError('Nie udało się załadować danych z arkusza INSPEKT.');
      setInspektSheet(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  useEffect(() => {
    try {
      if (dateFrom) localStorage.setItem(STORAGE_KEY_INSPEKT_DATE_FROM, dateFrom);
      if (dateTo) localStorage.setItem(STORAGE_KEY_INSPEKT_DATE_TO, dateTo);
    } catch (e) {}
  }, [dateFrom, dateTo]);

  const headers = inspektSheet?.data?.[0]?.map((c) => (c && typeof c === 'object' ? c.value : c)) ?? [];
  const dataRows = inspektSheet?.data?.slice(1) ?? [];
  const filteredRows = filterRowsByDateRange(dataRows, headers, dateFrom, dateTo);

  const defektCol = getColumnIndexFlex(headers, 'defekt', 'Defekt');
  const dzialCol = getColumnIndexFlex(headers, 'dział', 'Dział');
  const uniqueDefekty = new Set(filteredRows.map((r) => String((r[defektCol] && typeof r[defektCol] === 'object' ? r[defektCol].value : r[defektCol]) ?? '').trim()).filter(Boolean));
  const uniqueDzialy = new Set(filteredRows.map((r) => normalizeDzial(r[dzialCol] && typeof r[dzialCol] === 'object' ? r[dzialCol].value : r[dzialCol])).filter(Boolean));

  const kpiValues = {
    'ilosc-wpisow': filteredRows.length,
    'defektow': uniqueDefekty.size,
    'dzialow': uniqueDzialy.size,
  };

  const kpiCards = KPI_CARDS_KEYS.map((k) => ({
    ...k,
    value: kpiValues[k.key] ?? '—',
  }));

  const rowsForCharts =
    rangeKey === 'day' && selectedTimeChartDateStr
      ? filterRowsByDateStr(filteredRows, headers, selectedTimeChartDateStr)
      : rangeKey === 'month' && selectedTimeChartMonth != null && selectedTimeChartYear != null
        ? filterRowsByMonth(filteredRows, headers, selectedTimeChartYear, selectedTimeChartMonth)
        : rangeKey === 'year' && selectedTimeChartYear != null
          ? filterRowsByYear(filteredRows, headers, selectedTimeChartYear)
          : filteredRows;

  const chartDefektData = inspektSheet ? buildDefektBarData(rowsForCharts, headers) : [];
  const chartDzialData = inspektSheet ? buildDzialBarData(rowsForCharts, headers) : [];

  const timeChartData =
    inspektSheet && headers.length
      ? buildTimeChartDataByDateRange(filteredRows, headers, dateFrom, dateTo, rangeKey)
      : [];

  const tableRows = filterRowsByBarSelection(
    rowsForCharts,
    headers,
    selectedDefekt,
    selectedDzial
  );
  const hasBarFilter = selectedDefekt != null || selectedDzial != null;
  const clearBarFilter = () => {
    setSelectedDefekt(null);
    setSelectedDzial(null);
  };
  const hasTimeChartFilter =
    (rangeKey === 'day' && selectedTimeChartDateStr) ||
    (rangeKey === 'month' && selectedTimeChartMonth != null && selectedTimeChartYear != null) ||
    (rangeKey === 'year' && selectedTimeChartYear != null);
  const clearTimeChartFilter = () => {
    setSelectedTimeChartDateStr(null);
    setSelectedTimeChartMonth(null);
    setSelectedTimeChartYear(null);
  };

  /** Render komórki tabeli – dla kolumny Dział usuń prefix C_ */
  const renderTableCell = (cell, colIdx) => {
    const h = headers[colIdx];
    const isDzialCol = h && (String(h).toLowerCase().includes('dział') || String(h).toLowerCase().includes('dzial'));
    const raw = cell && typeof cell === 'object' ? cell.value : cell;
    const display = isDzialCol ? normalizeDzial(raw) : raw;
    return String(display ?? '');
  };

  return (
    <div className="kpi-stat-page">
      <div className="kpi-ps-header-bar">
        <Title className="kpi-ps-header-title">INSPEKT</Title>
        <div className="kpi-ps-zakres">
          <span className="kpi-ps-zakres-title">Zakres dat</span>
          <div className="kpi-ps-zakres-inner">
            <DatePicker
              label="Od:"
              value={dateFrom}
              onChange={(v) => setDateFrom(v || '')}
              max={dateTo || undefined}
              placeholder="Od"
              inline
              align="left"
              className="kpi-ps-zakres-date"
            />
            <DatePicker
              label="Do:"
              value={dateTo}
              onChange={(v) => setDateTo(v || '')}
              min={dateFrom || undefined}
              placeholder="Do"
              inline
              align="right"
              className="kpi-ps-zakres-date"
            />
          </div>
          <span className="kpi-ps-zakres-title" style={{ marginLeft: 8 }}>Wykres czasu</span>
          <div className="kpi-ps-zakres-buttons" role="group" aria-label="Grupowanie wykresu czasu">
            {ZAKRES_OPTIONS.map((o) => (
              <button
                key={o.key}
                type="button"
                className={`kpi-ps-zakres-btn ${rangeKey === o.key ? 'kpi-ps-zakres-btn-active' : ''}`}
                onClick={() => {
                  setRangeKey(o.key);
                  setSelectedTimeChartDateStr(null);
                  setSelectedTimeChartMonth(null);
                  setSelectedTimeChartYear(null);
                }}
                aria-pressed={rangeKey === o.key}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <Card className="kpi-ps-shell">
        <Grid numItemsSm={2} numItemsMd={3} numItemsLg={6} className="kpi-ps-cards">
          {kpiCards.map((kpi) => (
            <Card key={kpi.key} className="kpi-ps-kpi-card">
              <Text className="kpi-ps-kpi-label">{kpi.label}</Text>
              <Metric className="kpi-ps-kpi-metric">{kpi.value}</Metric>
            </Card>
          ))}
        </Grid>
      </Card>

      <Card className="kpi-ps-charts-area">
        <div className="kpi-ps-chart-time-block">
          <Text className="kpi-ps-chart-block-title">Ilość wpisów wg dat</Text>
          {timeChartData.length === 0 ? (
            <div className="kpi-ps-chart-empty">Brak danych do wykresu czasu w wybranym zakresie.</div>
          ) : (
            <div
              className="kpi-ps-chart-time"
              onMouseDown={() => {
                const el = document.activeElement;
                if (el?.closest?.('.kpi-ps-charts-area')) el.blur();
              }}
              onMouseUp={() => {
                setTimeout(() => {
                  const el = document.activeElement;
                  if (el?.closest?.('.kpi-ps-charts-area')) el.blur();
                }, 0);
              }}
            >
              <ResponsiveContainer width="100%" height={280}>
                <RechartsBarChart
                  data={timeChartData}
                  margin={{ top: 28, right: 24, left: 8, bottom: 8 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" strokeOpacity={0.6} />
                  <XAxis
                    dataKey="label"
                    angle={-45}
                    textAnchor="end"
                    height={48}
                    tick={{ fill: '#94a3b8', fontSize: 11 }}
                    interval={0}
                  />
                  <YAxis allowDecimals={false} tick={{ fill: '#94a3b8', fontSize: 12 }} />
                  <Tooltip content={<InspektTooltip />} cursor={false} />
                  <Bar
                    dataKey="value"
                    name="Ilość wpisów"
                    fill={CHART_COLORS[0]}
                    radius={[4, 4, 0, 0]}
                    barSize={24}
                    onClick={
                      rangeKey === 'day'
                        ? (data) => setSelectedTimeChartDateStr((prev) => (prev === data.dateStr ? null : data.dateStr))
                        : rangeKey === 'month'
                          ? (data) => {
                              const same = selectedTimeChartMonth === data.month && selectedTimeChartYear === data.year;
                              setSelectedTimeChartMonth(same ? null : data.month);
                              setSelectedTimeChartYear(same ? null : data.year);
                            }
                          : rangeKey === 'year'
                            ? (data) => setSelectedTimeChartYear((prev) => (prev === data.year ? null : data.year))
                            : undefined
                    }
                    style={rangeKey === 'day' || rangeKey === 'month' || rangeKey === 'year' ? { cursor: 'pointer' } : undefined}
                  >
                    {timeChartData.map((_, i) => (
                      <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                    ))}
                    <LabelList dataKey="value" position="top" fill="#94a3b8" fontSize={11} formatter={(v) => (v > 0 ? String(Math.round(Number(v))) : '')} />
                  </Bar>
                </RechartsBarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        {/* INSPEKT według Defekt – w osobnej linii na pełną szerokość dla lepszej czytelności */}
        <div className="kpi-ps-chart-block kpi-inspekt-defekt-block">
          <Text className="kpi-ps-chart-block-title">INSPEKT według Defekt</Text>
          {chartDefektData.length === 0 ? (
            <div className="kpi-ps-chart-empty">Brak danych w wybranym zakresie.</div>
          ) : (
            <div
              className="kpi-ps-chart-opis"
              onMouseDown={() => {
                const el = document.activeElement;
                if (el?.closest?.('.kpi-ps-charts-area')) el.blur();
              }}
              onMouseUp={() => {
                setTimeout(() => {
                  const el = document.activeElement;
                  if (el?.closest?.('.kpi-ps-charts-area')) el.blur();
                }, 0);
              }}
            >
              <ResponsiveContainer width="100%" height={Math.max(280, chartDefektData.length * 40)}>
                <RechartsBarChart
                  layout="vertical"
                  data={chartDefektData}
                  margin={{ top: 8, right: 24, left: 16, bottom: 8 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" strokeOpacity={0.6} />
                  <XAxis type="number" tick={{ fill: '#94a3b8', fontSize: 12 }} />
                  <YAxis
                    type="category"
                    dataKey="name"
                    width={320}
                    tick={{ fill: '#e2e8f0', fontSize: 12, fontWeight: 500 }}
                    tickFormatter={(v) => (v && String(v).length > 50 ? `${String(v).slice(0, 47)}…` : v)}
                    interval={0}
                  />
                  <Tooltip content={<DefektTooltip />} cursor={false} />
                  <Bar
                    dataKey="value"
                    name="Ilość wpisów"
                    radius={[0, 4, 4, 0]}
                    barSize={28}
                    onClick={(data) => setSelectedDefekt((prev) => (prev === data.name ? null : data.name))}
                    style={{ cursor: 'pointer' }}
                  >
                    {chartDefektData.map((_, i) => (
                      <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                    ))}
                  </Bar>
                </RechartsBarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        <div className="kpi-ps-charts-grid kpi-inspekt-charts-grid">
          <div className="kpi-ps-chart-block">
            <Text className="kpi-ps-chart-block-title">INSPEKT według Dział</Text>
            {chartDzialData.length === 0 ? (
              <div className="kpi-ps-chart-empty">Brak danych w wybranym zakresie.</div>
            ) : (
              <div
                className="kpi-ps-chart-psa kpi-ps-chart-psa-rounded"
                onMouseDown={() => {
                  const el = document.activeElement;
                  if (el?.closest?.('.kpi-ps-charts-area')) el.blur();
                }}
                onMouseUp={() => {
                  setTimeout(() => {
                    const el = document.activeElement;
                    if (el?.closest?.('.kpi-ps-charts-area')) el.blur();
                  }, 0);
                }}
              >
                <ResponsiveContainer width="100%" height={280}>
                  <RechartsBarChart
                    data={chartDzialData}
                    margin={{ top: 8, right: 24, left: 8, bottom: 8 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" strokeOpacity={0.6} />
                    <XAxis dataKey="DZIAŁ" angle={-45} textAnchor="end" height={48} tick={{ fill: '#94a3b8', fontSize: 11 }} />
                    <YAxis allowDecimals={false} tick={{ fill: '#94a3b8', fontSize: 12 }} />
                    <Tooltip content={<DzialTooltip />} cursor={false} />
                    <Bar
                      dataKey="value"
                      name="Ilość wpisów"
                      radius={[4, 4, 0, 0]}
                      barSize={24}
                      onClick={(data) => setSelectedDzial((prev) => (prev === data.DZIAŁ ? null : data.DZIAŁ))}
                      style={{ cursor: 'pointer' }}
                    >
                      {chartDzialData.map((_, i) => (
                        <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                      ))}
                    </Bar>
                  </RechartsBarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </div>
      </Card>

      <Card className="kpi-ps-table-card">
        <Title className="kpi-ps-table-title">Lista wpisów INSPEKT</Title>
        {hasTimeChartFilter && (
          <div className="kpi-ps-table-filter-hint">
            <span>
              {rangeKey === 'day' && selectedTimeChartDateStr && `Dane za dzień ${selectedTimeChartDateStr}`}
              {rangeKey === 'month' && selectedTimeChartMonth != null && selectedTimeChartYear != null && `Dane za miesiąc ${MONTH_LABELS[selectedTimeChartMonth - 1]} ${selectedTimeChartYear}`}
              {rangeKey === 'year' && selectedTimeChartYear != null && `Dane za rok ${selectedTimeChartYear}`}
              {' (wybór z wykresu „Ilość wpisów wg dat").'}
            </span>
            <Button size="xs" variant="secondary" onClick={clearTimeChartFilter} className="kpi-ps-table-clear-filter">
              Pokaż cały zakres
            </Button>
          </div>
        )}
        {hasBarFilter && (
          <div className="kpi-ps-table-filter-hint">
            <span>
              {selectedDefekt && `Defekt: ${selectedDefekt}`}
              {selectedDefekt && selectedDzial && ' · '}
              {selectedDzial && `Dział: ${selectedDzial}`}
            </span>
            <Button size="xs" variant="secondary" onClick={clearBarFilter} className="kpi-ps-table-clear-filter">
              Wyczyść filtr
            </Button>
          </div>
        )}
        {error && <Text className="kpi-ps-table-error">{error}</Text>}
        {loading ? (
          <Text className="kpi-ps-table-loading">Ładowanie…</Text>
        ) : !inspektSheet ? (
          <Text className="kpi-ps-table-empty">Brak arkusza „INSPEKT” w Uzupełnij raport. Dodaj arkusz o nazwie INSPEKT z kolumnami: Dział, Data, Komentarz, Defekt i wypełnij dane.</Text>
        ) : (
          <Table className="kpi-ps-table">
            <TableHead>
              <TableRow>
                {headers.map((h, i) => (
                  <TableHeaderCell key={i}>{h || `Kolumna ${i + 1}`}</TableHeaderCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {tableRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={headers.length} className="kpi-ps-table-empty-cell">
                    {filteredRows.length === 0
                      ? 'Brak wpisów w wybranym zakresie.'
                      : 'Brak wpisów spełniających wybór z wykresu. Kliknij inny słupek lub „Wyczyść filtr”.'}
                  </TableCell>
                </TableRow>
              ) : (
                tableRows.map((row, idx) => (
                  <TableRow key={idx}>
                    {row.map((cell, c) => (
                      <TableCell key={c}>{renderTableCell(cell, c)}</TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}

export default Inspekt;
